"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { api, type Referral } from "@/lib/api";
import { ModalOverlay } from "@/components/ModalOverlay";

// Sidebar box (pinned just above Settings) whose button opens the referral
// modal. A referred friend who registers with the code AND later upgrades to a
// paid plan earns BOTH sides one free month of Pro — the reward is granted on
// the paid conversion, never at sign-up.
export function InviteFriend() {
  const t = useTranslations("invite");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Referral | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState<"code" | "link" | null>(null);

  function openModal() {
    setOpen(true);
    if (!data && !loading) {
      setLoading(true);
      setError(false);
      api.getReferral()
        .then(setData)
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }
  }

  async function copy(value: string, which: "code" | "link") {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
      setTimeout(() => setCopied((c) => (c === which ? null : c)), 1800);
    } catch {
      /* clipboard unavailable — the value is still visible to copy manually */
    }
  }

  return (
    <>
      {/* The box, above the Settings item */}
      <div className="px-2 md:px-3 pb-2">
        <button
          onClick={openModal}
          title={t("button")}
          className="w-full flex items-center gap-2 rounded-xl border border-[var(--accent)] bg-[var(--accent-light)] px-2.5 md:px-3 py-2.5 text-sm font-semibold text-[var(--accent)] transition-colors hover:bg-[var(--accent)] hover:text-white justify-center md:justify-start"
        >
          <svg className="w-[18px] h-[18px] shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          </svg>
          <span className="hidden md:inline whitespace-nowrap">{t("button")}</span>
        </button>
      </div>

      {/* Modal */}
      {open && (
        <ModalOverlay
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-[var(--surface)] border border-[var(--border)] shadow-xl p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-1">
              <h2 className="text-lg font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-lora)" }}>
                {t("title")}
              </h2>
              <button
                onClick={() => setOpen(false)}
                aria-label={t("close")}
                className="shrink-0 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mb-5">{t("description")}</p>

            {loading && (
              <p className="text-sm text-[var(--text-muted)] py-6 text-center">{t("loading")}</p>
            )}
            {error && (
              <p className="text-sm text-red-500 py-6 text-center">{t("error")}</p>
            )}

            {data && (
              <div className="space-y-5">
                {/* Referral code */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
                    {t("codeLabel")}
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2.5 text-lg font-semibold tracking-[0.2em] text-[var(--text-primary)] text-center">
                      {data.code}
                    </code>
                    <button
                      onClick={() => copy(data.code, "code")}
                      className="shrink-0 rounded-xl border border-[var(--accent)] px-3.5 py-2.5 text-sm font-semibold text-[var(--accent)] hover:bg-[var(--accent-light)] transition-colors"
                    >
                      {copied === "code" ? t("copied") : t("copy")}
                    </button>
                  </div>
                </div>

                {/* Shareable link */}
                {data.link && (
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
                      {t("linkLabel")}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={data.link}
                        onFocus={(e) => e.currentTarget.select()}
                        className="flex-1 min-w-0 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] px-4 py-2.5 text-sm text-[var(--text-secondary)]"
                      />
                      <button
                        onClick={() => copy(data.link, "link")}
                        className="shrink-0 rounded-xl border border-[var(--accent)] px-3.5 py-2.5 text-sm font-semibold text-[var(--accent)] hover:bg-[var(--accent-light)] transition-colors"
                      >
                        {copied === "link" ? t("copied") : t("copy")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Reward explanation */}
                <div className="rounded-xl border border-[var(--border)] bg-[var(--accent-light)] px-4 py-3">
                  <p className="text-sm text-[var(--accent)] font-medium">{t("rewardHeadline")}</p>
                  <p className="text-xs text-[var(--text-secondary)] mt-1">{t("rewardDetail")}</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[var(--border)] px-4 py-3 text-center">
                    <div className="text-2xl font-semibold text-[var(--text-primary)]">{data.invited_count}</div>
                    <div className="text-xs text-[var(--text-muted)] mt-0.5">{t("invited")}</div>
                  </div>
                  <div className="rounded-xl border border-[var(--border)] px-4 py-3 text-center">
                    <div className="text-2xl font-semibold text-[var(--accent)]">{data.rewarded_count}</div>
                    <div className="text-xs text-[var(--text-muted)] mt-0.5">{t("rewarded")}</div>
                  </div>
                </div>

                {data.bonus_active && (
                  <p className="text-xs text-center text-emerald-600 font-medium">{t("bonusActive")}</p>
                )}
              </div>
            )}
          </div>
        </ModalOverlay>
      )}
    </>
  );
}
